import React, { useEffect, useState } from 'react';
import { Drawer, Box, IconButton, Typography, Grid } from '@mui/material';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import useWindowsDimension from 'functions/useWindowsDimension';
import { useAtom } from 'jotai';
import accessTokenAtom from 'data/atoms/accessTokenAtom';
import {
  ICompanyViewModel,
  IDepartmentViewModel,
  ThirdPartEnum,
} from 'Interfaces/ICompaniesConfig';

import {
  CloudFactoryResourceAddListViewModel,
  CloudFactoryResourceAddViewModel,
} from '../Converts/ICloudFactoryInterfaces';
import { useSnackbar } from 'notistack';
import { CloudFactoryService } from '../APi/CloudFactoryAPI';
import userAtom from 'data/atoms/userAtom';
import { ImportResourcesList } from '../ImportResourcesList';

// 🔹 Ny: bruk felles axios-klient med interceptor
import { api } from 'http/apiClient';

interface Props {
  open: boolean;
  company: ICompanyViewModel | null;
  onClose: () => void;
}

export const CloudFactoryResourceDrawer: React.FC<Props> = ({
  open,
  company,
  onClose,
}) => {
  const { height } = useWindowsDimension();
  const [user] = useAtom(userAtom);
  const [accessToken] = useAtom(accessTokenAtom);
  const [resources, setResources] = useState<
    CloudFactoryResourceAddViewModel[] | null
  >(null);
  const [departments] = useState<IDepartmentViewModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const load = async () => {
      setResources(null);

      if (!open || !company?.thirdPartyId || !accessToken) return;

      setLoading(true);
      setError(null);

      try {
        // 1) Hent ressursene fra CloudFactory
        const subs = await CloudFactoryService.getEmployees(
          company.thirdPartyId,
          accessToken
        );

        console.log('CloudFactory resources:', subs.data);

        enqueueSnackbar(
          `Subscriptions ${company.name}. funnet: ${subs.data.length}`,
          { variant: 'info' }
        );

        // 2) Hent lisenser for hver ressurs og bygg import-liste
        const importList: CloudFactoryResourceAddViewModel[] = [];

        for (const res of subs.data) {
          try {
            console.log('CF employee:', res);

            const cat = await CloudFactoryService.getEmployeeLicenses(
              company.thirdPartyId,
              res.id,
              accessToken
            );

            console.log('CF employee licenses:', cat);

            const newListItem: CloudFactoryResourceAddViewModel = {
              name: res.displayName,
              creatorId: user?.id ?? '',
              companyId: company.id,
              contactId: null,
              email: res.username,
              resourceTypeId: null,
              departmentId: res.departmentId ?? null,
              legacyId: 0,
              thirdPartyId: res.id,
              thirdParty: ThirdPartEnum.CloudFactory,
              isEmailFoundInBluu: false,
              isNotEmailInCloudFactory: false,
              productIds: [],
            };

            if (cat.data.length > 0) {
              newListItem.productIds = cat.data.map(
                (p: any) => p.displayName as string
              );
            }

            importList.push(newListItem);

            enqueueSnackbar(
              `Henter ${res.displayName} sine produkter: funnet ${cat.data.length}`,
              { variant: 'info' }
            );
          } catch (innerErr) {
            console.error('Feil i CF-employee-licences:', innerErr);
            enqueueSnackbar(`Kunne ikke hente katalog for ${res.displayName}`, {
              variant: 'error',
            });
          }
        }

        console.log('CloudFactory importList:', importList);

        // 3) Sammenlign mot backend (Bluu admin-API)
        //    Bruker api-klienten -> Authorization + X-Tenant-Id blir lagt på automatisk
        const response = await api.post<CloudFactoryResourceAddListViewModel>(
          '/Module/CloudFactory/ComparedResources',
          {
            numberOfCloudFactoryResources: importList.length,
            cloudFactoryResources: importList,
          }
        );

        console.log('ComparedResources response:', response.data);

        const result = response.data;

        // Filtrer bort EXT-brukere
        const filtered = result.cloudFactoryResources.filter(
          (e) => !e.email.toUpperCase().includes('EXT')
        );

        setResources(filtered);

        enqueueSnackbar(
          `Sjekket ${result.numberOfCloudFactoryResources} ansatte mot databasen`,
          { variant: 'success' }
        );
      } catch (err: any) {
        console.error('Feil i CloudFactoryResourceDrawer.load:', err);
        setError(err.message || 'Feil under lasting');
        enqueueSnackbar('Feil under lasting av CloudFactory-resurser', {
          variant: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, company, accessToken, enqueueSnackbar, user]);

  const handleRemoveResources = (
    imported: CloudFactoryResourceAddViewModel[]
  ) => {
    setResources((prev) =>
      prev ? prev.filter((p) => !imported.some((i) => i.email === p.email)) : []
    );

    enqueueSnackbar(
      `Fjernet ${imported.length} employees fra importlisten`,
      { variant: 'info' }
    );

    onClose();
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      variant="persistent"
      PaperProps={{ sx: { width: '100%' } }}
      onClose={onClose}
    >
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          sx={{
            flexGrow: 1,
            fontWeight: 'bold',
            fontSize: 20,
            color: 'primary.contrastText',
          }}
        >
          IMPORT RESOURCES
        </Typography>
        <IconButton onClick={onClose}>
          <CancelRoundedIcon />
        </IconButton>
      </Box>

      <Grid container sx={{ backgroundColor: 'primary.main' }}>
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'flex-start',
          }}
        >
          <ImportResourcesList
            expanded
            employees={resources || []}
            departments={departments}
            company={company}
            handleRemoveResources={handleRemoveResources}
          />
        </Grid>
      </Grid>
    </Drawer>
  );
};
